<script lang="ts">
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import type { MetaData } from "$lib/types/metaData";
  import Breadcrumb from "$lib/Breadcrumb.svelte";
  import Meta from "$lib/MetaData.svelte";
  import {
    productCategoryMapping,
    type Product,
    type ProductCategoryArticle,
  } from "$lib/types/products";
  import ListProducts from "$lib/ListProducts.svelte";
  import ProductFooter from "./ProductFooter.svelte";
  import ProductTabs from "./ProductTabs.svelte";
  import ProductImages from "./ProductImages.svelte";
  import CategoryArticle from "./CategoryArticle.svelte";
  import DiscountBanner from "../DiscountBanner.svelte";
  import PortableTextContent from "$lib/portableText/PortableTextContent.svelte";
  import KundeserviceBanner from "$lib/KundeserviceBanner.svelte";
  import CallMeButton from "$lib/CallMeButton.svelte";
  import Button from "$lib/Button.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";
  import CampaignBanner from "../CampaignBanner.svelte";
  import sessionValues from "$lib/sessionValues";
  import type { CampaignBanner as Banner } from "$lib/types/shared";
  import ProductVariants from "./ProductVariants.svelte";
  import Faq from "$lib/FAQ.svelte";

  const campaignDiscountPrice = 1000;

  interface Props {
    data: {
      meta?: MetaData;
      product: Readonly<Product>;
      categoryArticles: Readonly<ProductCategoryArticle>[];
      banner: Banner;
    };
  }

  let { data }: Props = $props();

  let source = $derived($sessionValues.source || "internett");
  let product = $derived(data?.product);
  let categoryArticles = $derived(data?.categoryArticles);
  let productCategory = $derived(
    productCategoryMapping.find((x) => product.product_categories.some((cat) => cat === x.key))?.key
  );
  let additionalCategory = $derived(product?.additional_category);
  let discountEligible = $derived(
    (source === "bonord" &&
      (productCategory === "varmepumpe" ||
        additionalCategory === "sikringsskap" ||
        productCategory === "elbillader")) ||
      (source === "strawberry" && productCategory === "varmepumpe")
  );

  let campaignDiscountActive = $derived(false);
  // new Date("2023-08-01T08:00:00") <= new Date() && product.typeform_name === "Norgespumpa 6.4";
  let showCampaignBanner = $derived(
    data?.banner?.active &&
      // @ts-expect-error _id is not typed
      data?.banner?.product?.some((x) => x._id === product._id)
  );
</script>

<Meta
  data={{
    title: product.title,
    description: product.description_short,
    keywords: productCategory ? [productCategory] : [],
    type: "product",
    price: product.price,
    campaign: campaignDiscountActive
      ? {
          name: "Kampanjepris",
          price: product.price - campaignDiscountPrice,
          // validFrom: "2021-08-01T08:00:00",
          // validThrough: "2023-08-01T08:00:00",
        }
      : undefined,
    ...(data?.meta || {}),
  }}
  jsonLd={true}
/>

<Breadcrumb
  breadcrumbs={[
    { label: "Forside", href: "/" },
    { label: "Nettbutikk", href: `/nettbutikk` },
    {
      label: productCategory
        ? productCategory.charAt(0).toUpperCase() + productCategory.slice(1)
        : "",
      href: productCategory ? `/nettbutikk/kategori/${productCategory}` : "/nettbutikk",
    },
    { label: product.title, href: `/nettbutikk/${product.slug.current}` },
  ]}
/>

<div class="mx-edge-x laptop:mt-5 product-header-grid laptop:grid gap-2">
  <h1 class="area-h1 mb-3 text-3xl">{product.title}</h1>

  <div class="area-visuals flex flex-col">
    {#if product.images?.length > 0}
      <ProductImages
        class="mt-1 laptop:mt-6 area-images"
        images={product.images}
        thumbnails={product.thumbnails}
        sizes={`(min-width: ${br.desktop}) 45vw, (min-width: ${br.laptop}) 50vw, 100vw`}
      />
    {/if}

    {#if product.alternative_variants?.length || 0 > 0}
      <ProductVariants
        class="mt-2 mb-10 area-variants"
        variants={product.alternative_variants}
        sizes={`(min-width: ${br.desktop}) 20vw, (min-width: ${br.laptop}) 25vw, 45vw`}
      />
    {/if}
  </div>

  <div class="area-content">
    {#if showCampaignBanner}
      <CampaignBanner class="mt-4 mb-6 text-start laptop:px-4" campaignBanner={data.banner} />
    {/if}
    <DiscountBanner class="mt-4 mb-6 laptop:px-4" />

    <p class="text-lead whitespace-pre-line">
      {product.description}
    </p>

    <div class="my-8 children-my-1">
      <p class="text-h2 text-$primary-strong">
        {#if campaignDiscountActive}
          <strong>
            {(product.price - campaignDiscountPrice).toLocaleString("nb-NO")},-
          </strong>
          <span class="text-body line-through ml-2">
            {product.price.toLocaleString("nb-NO")},-
          </span>
        {:else if productCategory === "gavekort"}
          <strong> Beløp fra 100,- </strong>
        {:else if productCategory === "elektriker" && additionalCategory === "elektriker"}
          <strong> Inntil 30 000 kr </strong>
        {:else}
          <strong>
            {product.price.toLocaleString("nb-NO")},-
          </strong>
        {/if}
      </p>

      {#if product.down_payment && additionalCategory === "elektriker"}
        <p class="text-body">Delbetaling over strømregninga i inntil 3 år</p>
      {:else if product.down_payment}
        <p class="text-body">
          Delbetaling over strømregninga til <strong class="whitespace-nowrap"
            >{(product.price / (product.down_payment * 12)).toFixed(0)},- i mnd over {product.down_payment}
            år</strong
          >
        </p>
      {/if}
    </div>

    {#if product.energy_certificate}
      <div class="w-30 max-w-full">
        <img
          src={`/_assets/energy-certificates/${product.energy_certificate}.svg`}
          alt={`Energimerke ${product.energy_certificate}`}
        />
      </div>
    {/if}

    <div class="text-base children-m-0 mt-6 landscape:flex gap-x-4">
      {#if productCategory !== "gavekort"}
        <p>
          <Button link href="#produktdatablad" class="min-w-0 px-0 text-start">
            {#snippet iconStart()}
              <svg
                aria-hidden="true"
                width="20"
                height="14"
                viewBox="0 0 20 14"
                xmlns="http://www.w3.org/2000/svg"
                class="fill-current relative top-[-.075em]"
              >
                <path d="M13 14L11.6 12.6L16.2 8H0V6H16.2L11.6 1.4L13 0L20 7L13 14Z" />
              </svg>
            {/snippet}
            Produktdatablad</Button
          >
        </p>
      {/if}
      <p>
        <Button
          link
          class="min-w-0 px-0 text-start"
          href="/nettbutikk/kategori/{productCategory}/kjopsbetingelser{data.product
            .additional_category
            ? `?additional_category=${data.product.additional_category}`
            : ''}"
        >
          {#snippet iconStart()}
            <svg
              aria-hidden="true"
              width="20"
              height="14"
              viewBox="0 0 20 14"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current relative top-[-.075em]"
            >
              <path d="M13 14L11.6 12.6L16.2 8H0V6H16.2L11.6 1.4L13 0L20 7L13 14Z" />
            </svg>
          {/snippet}
          Se våre kjøpsbetingelser</Button
        >
      </p>
    </div>
    <p class="my-2 text-body">
      {#if product.product_categories.includes("varmepumpe") || product.product_categories.includes("varmtvannsbereder")}
        <TypeformButton
          url={`//ishavskraft.typeform.com/to/${product.product_categories.includes("varmepumpe") ? "m2jWCa5u" : "AnmdiZAY"}`}
          class="button-primary-success"
          {source}
          customParameters={{
            ...(!!(product?.typeform_name || product?.title) && {
              product: product?.typeform_name || product.title,
            }),
            ...(!!product?.typeform_id && { product_id: product.typeform_id }),
            ...(!!product?.campaign && { campaign: product.campaign }),
          }}
          arrow>Bestill{discountEligible ? " med bonus" : ""}</TypeformButton
        >
      {/if}
      {#if product.product_categories.includes("elektriker")}
        <Button
          rel="nofollow"
          href={`/nettbutikk/${product.slug.current}/bestill`}
          arrow
          class="button-primary-success"
        >
          Finn nærmeste elektriker og bestill
        </Button>
      {/if}
      {#if product.product_categories.includes("elbillader")}
        <Button
          rel="nofollow"
          href={`/nettbutikk/${product.slug.current}/bestill`}
          arrow
          class="button-primary-success"
        >
          Finn nærmeste installatør og bestill
        </Button>
      {/if}
      {#if productCategory === "gavekort"}
        <TypeformButton
          url="https://ishavskraft.typeform.com/to/uD8f4Rry"
          arrow
          class="button-primary-success"
        >
          Bestill
        </TypeformButton>
      {/if}
    </p>

    {#if productCategory !== "varmepumpe" && productCategory !== "gavekort"}
      <p class="text-body m-0">
        <Button
          arrow
          href={`/nettbutikk/kategori/${productCategory}/standard-installasjon${data.product.additional_category ? `?additional_category=${data.product.additional_category}` : ""}`}
          >Se hva som inngår i en standard installasjon</Button
        >
      </p>
    {/if}
  </div>
</div>

{#if productCategory !== "gavekort"}
  <div aria-hidden="true" class="h-4 bg-$bg-muted mt-12"></div>
{/if}

<div>
  <div class="bg-$bg-muted py-8">
    <PortableTextContent --line-height="1.5" content={product.content} />
    {#if productCategory !== "gavekort"}
      <ProductTabs {product} />
    {/if}
  </div>

  {#if productCategory === "varmepumpe"}
    <section class="px-edge-x bg-$primary-dark text-$bg py-6 mb-8 children-max-w-prose">
      <h2>Standard installasjon er inkludert</h2>

      <p class="font-bold">
        Du burde sette deg inn i
        <a href="/nettbutikk/kategori/{productCategory}/standard-installasjon"
          >hva som inngår i en standard installasjon</a
        >.
      </p>

      <p>Erfaringsmessig kan noe av dette komme i tillegg:</p>

      <ul class="children-my-3">
        <li>
          Kjøring fra montørens adresse hjem til deg, hvis dette er lengre enn 25 km (50 km tur/
          retur er inkludert).
        </li>
        <li>
          Dersom du ikke har en stikkontakt du kan bruke i nærheten av der pumpa skal stå, så må du
          ha en elektriker til å legge opp det. Dette bestiller du selv fra en elektriker.
        </li>
        <li>
          Hvis du har en varmepumpe fra før, så vil montøren fakturere 1590 kr for nedmontering og
          fjerning av den gamle varmepumpen (må gjøres av en fagperson).
        </li>
      </ul>
    </section>
  {/if}
  {#if productCategory === "gavekort"}
    <section class="px-edge-x bg-$primary-dark text-$bg py-6 mb-8 children-max-w-prose">
      <h2>Vilkår for kjøp av gavekort</h2>
      <ul class="children-my-3">
        <li>
          For å kjøpe et gavekort hos oss, må både du og den som skal få gavekortet være, eller bli,
          kunde hos Ishavskraft.
        </li>
        <li>Du kan kjøpe gavekort på strøm når det passer deg.</li>
        <li>
          Gavekortet er gyldig i hele 1 år. Vi håper dette kan bli en meningsfull gave som sprer
          både varme og glede i hverdagen.
        </li>
      </ul>
      <Button primary href="/nettbutikk/kategori/gavekort/kjopsbetingelser">Les mer her</Button>
    </section>
  {/if}

  {#if product?.faq}
    <!-- FAQ -->
    <section class="mx-edge-x mt-12 mb-8">
      <h2>{product.faq.heading || `Spørsmål og svar om ${product.title}`}</h2>
      <div class="border-solid border-3 rounded-2xl p-2">
        {#each product.faq.items as answer (answer)}
          <Faq {answer} />
        {/each}
      </div>
    </section>
  {/if}

  {#if categoryArticles}
    <CategoryArticle {categoryArticles} />
  {/if}

  <KundeserviceBanner title="Har du spørsmål om produktet?" />

  {#if product.related_products && product?.related_products?.length > 0}
    <section>
      <h2 class="px-edge-x">Relaterte produkter</h2>
      <ListProducts products={product.related_products} horizontalScroll={true} />
    </section>
  {/if}

  {#if product?.additional_content}
    <div class="[--text-body:var(--text-sm)]">
      <hr class="mx-edge-x mt-12 mb-16" />

      <PortableTextContent content={product.additional_content} />
    </div>

    <hr class="mx-edge-x mt-20 mb-12" />

    <ProductFooter sticky={true} {product} {discountEligible} {source} />
  {/if}
  <CallMeButton rotated />
</div>

<style lang="postcss">
  @media (--laptop) {
    .product-header-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: auto 1fr;
      grid-template-areas:
        "h1 visuals"
        "content visuals";
      gap: 0 var(--s-16);

      & :global(h1) {
        grid-area: h1;
      }

      & :global(.area-content) {
        grid-area: content;
      }

      & :global(.area-visuals) {
        grid-area: visuals;
      }
    }
  }
</style>
